<script lang="ts">
  import {
    BUTTON_SIZE,
    BUTTON_VARIANT,
    CARD_PANEL_VARIANT,
    PROGRAM_LISTING_SKIP,
  } from '$lib/constants/constants';
  import Breadcrumbs from '$lib/components/website/Breadcrumbs.svelte';
  import ThemedHeader from '$lib/components/website/ThemedHeader.svelte';
  import AccordionFilter from '$lib/components/website/accordion/AccordionFilter.svelte';
  import Accordion from '$lib/components/website/accordion/Accordion.svelte';
  import DropdownFilters from '$lib/components/website/dropdownFilters/DropdownFilters.svelte';
  import FilterIcon from '../../assets/svgs/filter-lines.svelte';
  import type { CardProps } from '$lib/types/contentTypes';
  import { goto } from '$app/navigation';
  import { page } from '$app/stores';
  import ClearFilterButton from '$lib/components/website/dropdownFilters/ClearFilterButton.svelte';
  import Button from '$lib/components/control/Button.svelte';
  import CardPanel from '$lib/components/website/CardPanel.svelte';

  export let data;

  let skip = PROGRAM_LISTING_SKIP;
  let morePrograms: CardProps[] = [];

  $: pageChange = $page.url;

  // pageChange && is triggering getQueryParamAsArray when pageChange happens
  $: selectedValues = pageChange && {
    'suitable-for': getQueryParamAsArray('suitable-for'),
    'training-style': getQueryParamAsArray('training-style'),
    trainer: getQueryParamAsArray('trainer'),
    location: getQueryParamAsArray('location'),
  };

  $: totalSelectedParamsKey = Object.keys(selectedValues).reduce((acc, selectedKey) => {
    // we need the number of selected filter type
    if (selectedValues[selectedKey].length) {
      acc += 1;
    }
    return acc;
  }, 0);

  $: totalNumberOfPrograms = data?.pageData?.paginatedProgram?.totalPrograms;
  $: displayLoadMoreButton =
    data?.pageData?.paginatedProgram?.programsList?.length + morePrograms?.length <
    totalNumberOfPrograms;

  const fetchMoreExercises = async () => {
    const pageSearchParams = $page?.url?.search || '';
    const searchParams =
      pageSearchParams?.indexOf('?') == 0 ? pageSearchParams.substring(1) : pageSearchParams;

    const res = await fetch(`/api/programs/?skip=${skip}&${searchParams}`);
    const moreProgramsResponse = await res.json();

    if (moreProgramsResponse?.length) {
      morePrograms = morePrograms.concat(moreProgramsResponse);
      skip = skip + PROGRAM_LISTING_SKIP;
    }
  };

  const resetMorePrograms = () => {
    skip = PROGRAM_LISTING_SKIP;
    morePrograms = [];
  };

  const handleClearFilters = () => {
    resetMorePrograms();
    return goto('/programs', {
      replaceState: false,
      noScroll: true,
      keepFocus: true,
      invalidateAll: true,
    });
  };

  const getQueryParamAsArray = (queryString: string) => {
    const targetedQueryString = $page.url?.searchParams?.get(queryString);

    const matchingDropdown = data?.pageData?.programDropdowns.find(({ id }) => id === queryString);

    if (targetedQueryString) {
      return targetedQueryString
        .split(',')
        .filter((queryString) =>
          matchingDropdown?.options?.some(({ value }) => value === queryString),
        );
      // this filter any params that would not belong to any dropdown values
    }

    return [];
  };

  const handleSelectProgram = (
    targetedQueryParam: string,
    dropdownValue: string,
    canSelectMultiple: boolean,
  ) => {
    resetMorePrograms();
    const currentParamValue = $page.url?.searchParams?.get(targetedQueryParam);
    const currentParamsArray = currentParamValue?.split(',');
    if (currentParamsArray && currentParamsArray.includes(dropdownValue)) {
      // INFO: When we unselect
      const newParams = currentParamsArray.filter((c) => c !== dropdownValue).join();
      $page.url.searchParams.set(targetedQueryParam, newParams);
      if (currentParamsArray?.length === 1) {
        /*
          we cannot use directly searchParams.delete(name, value) as it is not compatible with
          the per default ASCII url syntax we use.
          Therefore we need to use set with an empty value then delete the query params name if it is the last item
          eg:
          ?equipment=Chair%2CCable => no need to delete the query name
          ?equipment=Chair => Need delete the query name
        */
        $page.url.searchParams.delete(targetedQueryParam);
      }
    } else {
      // INFO: When we select
      let newQueryParams = '';
      if (canSelectMultiple) {
        /// INFO: when an dropdown can only have 1 selected item
        newQueryParams = currentParamValue
          ? `${currentParamValue},${dropdownValue}`
          : `${dropdownValue}`;
      } else {
        // INFO: when an dropdown can only have multiple selected items
        newQueryParams = dropdownValue;
      }
      $page.url.searchParams.set(targetedQueryParam, newQueryParams);
    }

    return goto(`?${$page.url.searchParams.toString()}`, {
      replaceState: false,
      noScroll: true,
      keepFocus: true,
      invalidateAll: true,
    });
  };
</script>

<div>
  <ThemedHeader
    backgroundColor="bg-primary-500"
    title={data?.pageData?.pageHeading}
    description={data?.pageData?.pageDescription}
  />
  <div class="flex flex-col p-4 max-w-1280 mx-auto mb-16 lg:mb-24 sm:p-8">
    <Breadcrumbs
      links={[
        { label: 'Sweat', href: '/' },
        { label: 'Programs', href: '/programs', isCurrentUrl: true },
      ]}
    />
    <section class=" flex flex-col gap-8 sm:gap-16">
      <h2
        class="font-semibold text-gray-900 text-display-xs text-center mt-6 sm:mt-8 sm:text-display-lg sm:text-left"
      >
        Find a program
      </h2>
      <div class="relative">
        {#if data?.pageData?.programDropdowns?.length}
          <DropdownFilters
            customClass="hidden items-center justify-center gap-8 sm:flex sm:flex-row flex-wrap"
            dropdowns={data?.pageData?.programDropdowns}
            handleSelect={handleSelectProgram}
            {selectedValues}
          />
          <Accordion customClass="flex sm:hidden">
            <AccordionFilter
              open={false}
              customClass="flex px-4 py-6 flex flex-col gap-2 bg-gray-50"
            >
              <div slot="title" class="flex flex-row items-center">
                <FilterIcon />
                <p class="text-gray-900 text-lg ml-4 font-semibold text-left">Filter</p>
                {#if totalSelectedParamsKey > 0}
                  <div class="bg-sweat-pink-50 ml-2 w-6 h-6 rounded-full text-white text-sm">
                    <span>{totalSelectedParamsKey}</span>
                  </div>
                  <div
                    tabindex="0"
                    role="button"
                    on:click={handleClearFilters}
                    class="flex sm:hidden ml-2 p-0 text-sm text-sweat-pink-50 font-semibold"
                  >
                    Clear All
                  </div>
                  <!-- Using a button rather than a div trigger a glitch for a small instant -->
                {/if}
              </div>
              <DropdownFilters
                slot="content"
                customClass="flex flex-col items-center justify-center gap-3 pt-6 sm:hidden"
                dropdowns={data?.pageData?.programDropdowns}
                handleSelect={handleSelectProgram}
                {selectedValues}
              />
            </AccordionFilter>
          </Accordion>
        {/if}
        {#if $page.url?.search?.length}
          <div
            class="hidden md:block sm:flex p-0 sm:-bottom-8 text-sm text-sweat-pink-50 font-semibold pt-4 -mb-8"
          >
            <ClearFilterButton {handleClearFilters} />
          </div>
        {/if}
      </div>
      {#if data?.pageData?.paginatedProgram.programsList?.length == 0}
        <div class="flex items-center flex-col py-16 lg:py-24 px-2">
          <div class="text-center">
            <span class="text-gray-600 text-xxl font-semibold"
              >No programs found. Try removing some filters.</span
            >
          </div>
        </div>
      {/if}
      {#if data?.pageData?.paginatedProgram.programsList?.length}
        <CardPanel
          cardsCollection={[...data?.pageData?.paginatedProgram?.programsList, ...morePrograms]}
          variant={CARD_PANEL_VARIANT.GRID}
          customGridPanelClass="grid grid-cols-1 gap-y-4 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16 pt-8 lg:pt-16"
          customContainer=""
        />
      {/if}
      {#if displayLoadMoreButton}
        <Button
          text="Load More"
          variant={BUTTON_VARIANT.OUTLINED}
          buttonSize={BUTTON_SIZE.LARGE}
          customClass="py-3 self-center"
          onClick={fetchMoreExercises}
        />
      {/if}
    </section>
  </div>
</div>
